(() => {
  const SECTION_SELECTOR = '[data-banner-icon-columns]';
  const instances = new WeakMap();

  const smallBreakpoint = () => window.theme?.sizes?.small ?? 768;

  class BannerIconColumns {
    constructor(container) {
      this.container = container;
      this.track = container.querySelector('[data-icon-cols-track]');
      this.items = [...container.querySelectorAll('[data-icon-col]')];
      this.dots = [...container.querySelectorAll('[data-icon-cols-dot]')];
      this.activeIndex = -1;
      this.rafId = null;
      this.controller = new AbortController();

      const { signal } = this.controller;

      this.track.addEventListener('scroll', () => this.queueSync(), { passive: true, signal });

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.scrollToIndex(index), { signal });
        dot.addEventListener(
          'keydown',
          (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            this.scrollToIndex(index);
          },
          { signal }
        );
      });

      this.setActive(0);
    }

    queueSync() {
      if (this.rafId) return;

      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;

        const width = this.track.clientWidth;
        if (!width) return;

        this.setActive(Math.round(this.track.scrollLeft / width));
      });
    }

    scrollToIndex(index) {
      const item = this.items[index];
      if (!item) return;

      const { track } = this;
      track.scrollLeft =
        item.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;

      this.setActive(index);
    }

    setActive(index) {
      if (index < 0 || index >= this.items.length || this.activeIndex === index) return;

      this.activeIndex = index;

      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-selected', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    highlight(index) {
      this.items.forEach((item, i) => item.classList.toggle('is-selected', i === index));
    }

    clearHighlight() {
      this.items.forEach((item) => item.classList.remove('is-selected'));
    }

    destroy() {
      this.controller.abort();
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }
  }

  const init = (root = document) => {
    const scope = root?.querySelectorAll ? root : document;
    const found = [...scope.querySelectorAll(SECTION_SELECTOR)];
    if (scope.matches?.(SECTION_SELECTOR)) found.unshift(scope);

    found.forEach((section) => {
      if (instances.has(section)) return;
      if (!section.querySelector('[data-icon-cols-track]')) return;
      if (section.querySelectorAll('[data-icon-col]').length < 2) return;

      instances.set(section, new BannerIconColumns(section));
    });
  };

  const instanceFor = (node) => {
    const section = node?.closest?.(SECTION_SELECTOR) ?? node?.querySelector?.(SECTION_SELECTOR);
    return section ? instances.get(section) : null;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    const instance = instanceFor(event.target);
    instance?.destroy();
  });

  document.addEventListener('shopify:block:select', (event) => {
    const instance = instanceFor(event.target);
    if (!instance) return;

    const index = instance.items.indexOf(event.target);
    if (index < 0) return;

    if (window.innerWidth < smallBreakpoint()) {
      instance.scrollToIndex(index);
    } else {
      instance.highlight(index);
    }
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    instanceFor(event.target)?.clearHighlight();
  });
})();
